import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import ApiService from '../../../../services/apiService';
import { useTranslation } from 'react-i18next';
import '../report.css';
import './table.css';
import { useNavigate } from 'react-router-dom';

const CashierSaleReport = () => {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ⭐ Branch states
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  // ⭐ Payment type states
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);

  const paymentTypes = [
    { label: 'All', value: '' }, // keep empty so no filter is applied
    { label: 'Cash', value: 1 }, // Cash = 1
    { label: 'Card', value: 8 }, // Card = 8
    { label: 'EFT', value: 9 }, // EFT = 9
    { label: 'Account', value: 10 }, // Account = 10
  ];

  // ⭐ Safe date formatter (YYYY-MM-DD)
  const getFormattedDate = (date) => {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };

  const today = new Date();
  const tenDaysAgo = new Date();
  tenDaysAgo.setDate(today.getDate() - 10);

  const [formData, setFormData] = useState({
    ReportType: { label: 'Users', value: 'Users' },
    fromDate: getFormattedDate(tenDaysAgo),
    toDate: getFormattedDate(today),
  });

  const reportType = [{ label: 'Users', value: 'Users' }];

  // -------------------------------------------------------------------
  // ✅ Safe display helpers (show "--" instead of blank / error)
  // -------------------------------------------------------------------
  const displayDash = (val) => {
    const s = String(val ?? '').trim();
    return s === '' ? '--' : s;
  };

  const formatAmount = (val) => {
    const raw = String(val ?? '').trim();
    if (raw === '') return '--';
    const n = Number(val);
    if (Number.isNaN(n)) return '--';
    return n.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatInt = (val) => {
    const raw = String(val ?? '').trim();
    if (raw === '') return '--';
    const n = Number(val);
    if (Number.isNaN(n)) return '--';
    return n;
  };

  // -------------------------------------------------------------------
  // ⭐ Fetch Branch List (GET_BRANCH)
  // -------------------------------------------------------------------
  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');
      const list = res?.data?.result || [];

      const mapped = list.map((b) => ({
        value: b.id,
        label: b.branchName,
      }));

      setBranches(mapped);
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  // -------------------------------------------------------------------
  // ⭐ Fetch Report Data (GET_SALE_SUMMERY)
  // -------------------------------------------------------------------
  const getReportData = async () => {
    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      params.append('ReportType', formData?.ReportType?.label || 'Users');
      params.append('StartDate', formData?.fromDate || '');
      params.append('EndDate', formData?.toDate || '');
      params.append('BranchId', selectedBranch?.value || 0); // 0 = all branches

      if (selectedPaymentType && selectedPaymentType.value !== '') {
        params.append('PaymentType', selectedPaymentType.value);
      }

      const res = await apiService.getApiParamater('GET_SALE_SUMMERY', `?${params.toString()}`);

      setData(res?.data?.result || []);
      setPagination(res?.data?.paginationData);
    } catch (error) {
      console.error('Error fetching report:', error);
      // Prevent white page
      setData([]);
      setPagination(undefined);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // -------------------------------------------------------------------
  // ⭐ Clean & group data (safe conversions)
  // -------------------------------------------------------------------
  const { employeesInOrder, groupedByEmployee, grandTotalAmount, grandTotalTrans } = useMemo(() => {
    const rawRows = Array.isArray(data) ? data : [];

    if (!rawRows.length) {
      return {
        employeesInOrder: [],
        groupedByEmployee: {},
        grandTotalAmount: 0,
        grandTotalTrans: 0,
      };
    }

    const cleanedRows = rawRows.filter((row) => {
      const pt = String(row?.['Payment Type'] ?? '').toLowerCase();
      return pt !== 'total' && pt !== 'grand total';
    });

    if (!cleanedRows.length) {
      return {
        employeesInOrder: [],
        groupedByEmployee: {},
        grandTotalAmount: 0,
        grandTotalTrans: 0,
      };
    }

    let lastEmpName = null;
    const normalizedRows = cleanedRows.map((row) => {
      let empName = row?.['Employee Name'];

      if (String(empName ?? '').trim() !== '') {
        lastEmpName = empName;
      } else if (lastEmpName) {
        empName = lastEmpName;
      } else {
        empName = 'Unknown';
      }

      return {
        ...row,
        __employee: empName,
      };
    });

    const grouped = {};
    const order = [];

    normalizedRows.forEach((row) => {
      const empName = row.__employee;
      if (!grouped[empName]) {
        grouped[empName] = [];
        order.push(empName);
      }
      grouped[empName].push(row);
    });

    let gAmount = 0;
    let gTrans = 0;

    order.forEach((empName) => {
      const rows = grouped[empName];

      const headerRow =
        rows.find((r) => {
          const hasName = String(r?.['Employee Name'] ?? '').trim() !== '';
          const hasTotals = !Number.isNaN(Number(r?.['Total Sale Amount by cashier'])) || !Number.isNaN(Number(r?.['Total Transactions']));
          return hasName || hasTotals;
        }) || rows[0];

      gAmount += Number(headerRow?.['Total Sale Amount by cashier'] || 0);
      gTrans += Number(headerRow?.['Total Transactions'] || 0);
    });

    return {
      employeesInOrder: order,
      groupedByEmployee: grouped,
      grandTotalAmount: gAmount,
      grandTotalTrans: gTrans,
    };
  }, [data]);

  // -------------------------------------------------------------------
  // ⭐ Render Component
  // -------------------------------------------------------------------
  return (
    <div className="p-lg-5">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      <div className="row g-3 align-items-end">
        {/* ⭐ Branch Dropdown */}
        <div className="col-3">
          <label className="form-label">Select Branch</label>
          <Select
            menuPortalTarget={document.body}
            value={selectedBranch}
            onChange={setSelectedBranch}
            options={branches}
            placeholder="Select Branch"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            isClearable
          />
        </div>

        {/* Report Type */}
        <div className="col-2">
          <label className="form-label">Report Type</label>
          <Select value={formData.ReportType} isDisabled onChange={(opt) => setFormData((prev) => ({ ...prev, ReportType: opt }))} options={reportType} />
        </div>

        {/* ⭐ Payment Type filter */}
        <div className="col-2">
          <label className="form-label">Payment Type</label>
          <Select
            menuPortalTarget={document.body}
            value={selectedPaymentType}
            onChange={setSelectedPaymentType}
            options={paymentTypes}
            placeholder="All"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            isClearable
          />
        </div>

        {/* From / To */}
        <div className="col-3">
          <label className="form-label">From / To</label>
          <div className="d-flex gap-2">
            <input type="date" className="form-control" value={formData?.fromDate || ''} name="fromDate" onChange={handleInputChange} />
            <input type="date" className="form-control" value={formData?.toDate || ''} name="toDate" onChange={handleInputChange} />
          </div>
        </div>

        {/* Search Button */}
        <div className="col-2 mt-3">
          <button className="mt-3 addItem-btn" onClick={getReportData}>
            {t('Search')}
          </button>
        </div>
      </div>

      {/* ------------------------------------------------------------------- */}
      {/* TABLE (unchanged) */}
      {/* ------------------------------------------------------------------- */}
      <div className="pt-5">
        <div className="POsgridshad p-0">
          <div className="custom-table-container">
            <table className="custom-table">
              <thead>
                <tr>
                  <th>Employee / Branch</th>
                  <th>Payment Type</th>
                  <th className="text-end">Amount</th>
                  <th className="text-end">Transactions</th>
                </tr>
              </thead>

              <tbody>
                {(!data || data.length === 0 || employeesInOrder.length === 0) && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-3">
                      No data found
                    </td>
                  </tr>
                )}

                {employeesInOrder.map((empName) => {
                  const rows = groupedByEmployee[empName] || [];

                  const headerRow =
                    rows.find((r) => {
                      const hasName = String(r?.['Employee Name'] ?? '').trim() !== '';
                      const hasTotals = !Number.isNaN(Number(r?.['Total Sale Amount by cashier'])) || !Number.isNaN(Number(r?.['Total Transactions']));
                      return hasName || hasTotals;
                    }) || rows[0];

                  const cashierTotalAmount = Number(headerRow?.['Total Sale Amount by cashier'] || 0);
                  const cashierTotalTrans = Number(headerRow?.['Total Transactions'] || 0);
                  const branchName = headerRow?.['Branch Name'];

                  return (
                    <React.Fragment key={empName}>
                      {rows.map((row, idx) => (
                        <tr key={`${empName}-${idx}`}>
                          <td>
                            {idx === 0 && (
                              <>
                                <div className="fw-semibold">{displayDash(empName)}</div>
                                <div className="text-muted small">{displayDash(branchName)}</div>
                              </>
                            )}
                          </td>

                          <td>{displayDash(row?.['Payment Type'])}</td>
                          <td className="text-end">{formatAmount(row?.['Total Amount'])}</td>
                          <td className="text-end">{formatInt(row?.['Payment Type Transactions'])}</td>
                        </tr>
                      ))}

                      <tr className="cashier-total-row">
                        <td className="fw-bold" />
                        <td className="text-end fw-bold">Total</td>
                        <td className="text-end fw-bold">{formatAmount(cashierTotalAmount)}</td>
                        <td className="text-end fw-bold">{formatInt(cashierTotalTrans)}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}

                {employeesInOrder.length > 0 && (
                  <tr className="grand-total-row">
                    <td className="fw-bold">Total Amount & Transactions</td>
                    <td />
                    <td className="text-end fw-bold">{formatAmount(grandTotalAmount)}</td>
                    <td className="text-end fw-bold">{formatInt(grandTotalTrans)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashierSaleReport;
